import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../ut_styles/colorz.scss';
import colorData from '../data/colorz.json';
import SvgIcn from "../data/IconCompo";
import { updateFavicon } from '../utils/favicon';
import BackButton from '../components/BackButton';

const Colorz = () => {
  useEffect(() => {
    updateFavicon('colorz');
  }, []);

  const [selectedColor, setSelectedColor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [showOrderOptions, setShowOrderOptions] = useState(false);
  const [orderBy, setOrderBy] = useState(() => {
    return localStorage.getItem('colorOrderPreference') || 'default';
  });
  const [noResultsColor, setNoResultsColor] = useState('');
  const [showHash, setShowHash] = useState(() => {
    return localStorage.getItem('showHashPreference') === 'true' || true;
  });
  const [hoveredColor, setHoveredColor] = useState(null);
  const [favorites, setFavorites] = useState(() => {
    return JSON.parse(localStorage.getItem('colorFavorites') || '[]');
  });
  const [showFavorites, setShowFavorites] = useState(false);
  const [filteredColors, setFilteredColors] = useState([...colorData.colors]);

  const orderColors = useCallback((colors, order) => {
    switch (order) {
      case 'name':
        return [...colors].sort((a, b) => a.name.localeCompare(b.name));
      case 'hex':
        return [...colors].sort((a, b) => a.hex.localeCompare(b.hex));
      case 'random':
        return [...colors].sort(() => Math.random() - 0.5);
      default:
        return colors;
    }
  }, []);

  // Memoize the favorites check function
  const isFavorite = useCallback((color) => {
    return favorites.some(fav => fav.hex === color.hex);
  }, [favorites]);

  // Memoize filtered colors
  const computedFilteredColors = useMemo(() => {
    let filtered = [...colorData.colors].filter(color => 
      color.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      color.hex.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (showFavorites) {
      filtered = filtered.filter(color => isFavorite(color));
    }

    return orderColors(filtered, orderBy);
  }, [searchTerm, orderBy, showFavorites, isFavorite, orderColors]);

  useEffect(() => {
    setFilteredColors(computedFilteredColors);
    
    if (computedFilteredColors.length === 0) {
      setNoResultsColor(generatePastelColor());
    }
  }, [computedFilteredColors]);

  const generatePastelColor = () => {
    const hue = Math.floor(Math.random() * 360);
    return `hsl(${hue}, 100%, 87%)`;
  };

  const handleColorClick = (color) => {
    setSelectedColor(selectedColor === color ? null : color);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000);
    });
  };

  const isLightColor = (hex) => {
    const rgb = parseInt(hex.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >>  8) & 0xff;
    const b = (rgb >>  0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    return luma > 128;
  };

  const handleOrderChange = (newOrder) => {
    setOrderBy(newOrder);
    localStorage.setItem('colorOrderPreference', newOrder);
    setShowOrderOptions(false);
  };

  const handleShowHashChange = () => {
    const newShowHash = !showHash;
    setShowHash(newShowHash);
    localStorage.setItem('showHashPreference', newShowHash.toString());
  };

  const getDisplayHex = (hex) => {
    return showHash ? hex : hex.substring(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const toggleFavorite = (e, color) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const isFavorite = favorites.some(fav => fav.hex === color.hex);
    let newFavorites;
    
    if (isFavorite) {
      newFavorites = favorites.filter(fav => fav.hex !== color.hex);
    } else {
      newFavorites = [...favorites, color];
    }
    
    setFavorites(newFavorites);
    localStorage.setItem('colorFavorites', JSON.stringify(newFavorites));
  };

  return (
    <div className="colorz-container">
      <BackButton />
      <div className="search-container">
        <input
          type="text"
          placeholder="Search colors..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
        <div 
          className={`search-icon ${searchTerm ? 'clear-icon' : ''}`}
          onClick={searchTerm ? clearSearch : undefined}
        >
          <SvgIcn name={searchTerm ? "clear" : "search"} />
          
        </div>
      </div>
      <div className="favorites-container">
        {showFavorites && (
          <div className="close-favorites-button" onClick={() => setShowFavorites(false)}>
            <SvgIcn name="close" />
          </div>
        )}
        <div className="favorites-button" onClick={() => setShowFavorites(!showFavorites)}>
          <SvgIcn name={showFavorites ? "heart2" : "heart"} />
          <span className="favorites-count">{favorites.length}</span>
        </div>
      </div>
      <div 
        className={`order-button ${showOrderOptions ? 'active' : ''}`} 
        onClick={() => setShowOrderOptions(!showOrderOptions)}
      >
        <SvgIcn name="setting_b" />
      </div>
      <div className={`order-options ${showOrderOptions ? 'visible' : ''}`}>
        <button 
          onClick={() => handleOrderChange('name')}
          className={orderBy === 'name' ? 'active' : ''}
        >
          Name A-Z
        </button>
        <button 
          onClick={() => handleOrderChange('hex')}
          className={orderBy === 'hex' ? 'active' : ''}
        >
          Hex
        </button>
        <button 
          onClick={() => handleOrderChange('random')}
          className={orderBy === 'random' ? 'active' : ''}
        >
          Random
        </button>
        <div className="switch-container">
        <span>Show #</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={showHash}
              onChange={handleShowHashChange}
            />
            <span className="slider"></span>
          </label>

        </div>
      </div>
      {filteredColors.length === 0 ? (
        <div className="no-results" style={{ backgroundColor: noResultsColor }}>
          <SvgIcn name="soon" />
          <p>No Color<br/> Found!</p>
        </div>
      ) : (
        <div className="colorz-grid">
          {filteredColors.map((color, index) => (
            <div
              key={index}
              className={`color-box ${hoveredColor === color ? 'hovered' : ''}`}
              style={{ backgroundColor: color.hex }}
              onMouseEnter={() => setHoveredColor(color)}
              onMouseLeave={() => setHoveredColor(null)}
            >
              <button 
                type="button"
                className={`favorite-btn ${isLightColor(color.hex) ? 'light-color' : ''} ${
                  favorites.some(fav => fav.hex === color.hex) ? 'is-favorite' : ''
                }`}
                onClick={(e) => toggleFavorite(e, color)}
                data-favorite={favorites.some(fav => fav.hex === color.hex)}
              >
                <SvgIcn name={favorites.some(fav => fav.hex === color.hex) ? "heart2" : "heart"} />
              </button>
              {hoveredColor === color && (
                <div className={`color-info ${isLightColor(color.hex) ? 'light-color' : ''}`}>
                  <span className="color-name">{color.name}</span>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(getDisplayHex(color.hex));
                    }}
                    style={{ 
                      transition: 'transform 0.1s',
                      ":active": {
                        transform: 'scale(0.95)'
                      }
                    }}
                  >
                    <span className="color-hex">{getDisplayHex(color.hex)}</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className={`tooltip_colorz ${showTooltip ? 'visible' : ''}`}>
        Copied to clipboard!
      </div>
    </div>
  );
};

export default Colorz;
